import FHIRUpdater from 'data/FHIRUpdater';
import SendGridPoster from 'data/SendGridPoster';
import type { Identifier, Patient } from 'fhir/r4';
import { EMAIL_TEMPLATES } from 'fhir-sdoh/common/constants';
import moment from 'moment';
import React from 'react';
import { List, Label, Loader, Popup, Checkbox, Icon } from 'semantic-ui-react';
import type { CheckboxProps, LabelProps } from 'semantic-ui-react';
import type { FlatCarePlan } from 'types';

import { useAuthContext } from '../../../../../contexts/AuthContext';
import { Referral, FHIRProxyClient } from '../../../../../fhir-sdoh';
import * as FHIRUtils from '../../../../../utils/FHIRUtils';
import type { StepCarePlanContext, StepMutate, StepPatientIdentity } from '../../types';


/**
 * Props for {@link Launch}.
 *
 * Unlike its four siblings, this step is rendered *unconditionally* by
 * `steps/index.tsx` — the others sit behind `{carePlan && ...}`. So `carePlan`
 * is genuinely absent here before a plan is launched, which is the state the
 * "Create Care Plan" branch exists to render.
 *
 * SUSPECTED DEFECT — `planDefinition` is read three ways in one component:
 * `props.planDefinition.id` / `.name` / `.purpose` unguarded inside `launch()`,
 * `props.planDefinition.name` unguarded in the completed-plan Popup, but
 * `props?.planDefinition?.name` guarded in the create-plan Popup. The guarded
 * read proves the author expected it to be absent; the unguarded ones then
 * throw in exactly that case. Reported, not fixed — adding `?.` to the launch
 * path would post a CarePlan with an undefined PlanDefinition id rather than
 * failing.
 */
type LaunchProps = StepPatientIdentity &
  StepCarePlanContext & {
    /** Fires after the CarePlan is created, so the parent can refetch. */
    onLaunch: StepMutate;
    /** Revalidates the email log after the primer/app-download sends. */
    communicationsMutate: StepMutate;
    /**
     * True while any parent refresh query is in flight; disables the control.
     *
     * Required: `isLoading` is `loading || props.isRefreshing`, so an absent
     * prop makes it `undefined` rather than `false`, and that reaches
     * semantic-ui `disabled` props which reject `undefined` under
     * `exactOptionalPropertyTypes`. `steps/index.tsx` always passes it.
     */
    isRefreshing: boolean;
  };

function Launch(props: LaunchProps) {
  const [loading, setLoading] = React.useState(false);
  const [enableLaunch, setEnableLaunch] = React.useState(false);

  const carePlan = props.carePlan;
  let flatCarePlan: FlatCarePlan | undefined;
  if (carePlan) {
    flatCarePlan = FHIRUtils.parseFHIRCarePlan(carePlan);
  }

  const authInfo = useAuthContext();

  const onAccept = (_e: React.FormEvent<HTMLInputElement>, { value: _value }: CheckboxProps) =>
    setEnableLaunch(!enableLaunch);

  const launch = async (_data?: unknown) => {
    if (!enableLaunch) {
      return;
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoading(true);

    try {
      //launch primer + app download emails
      if (props.orgId === 'IU') {
        let payload = {
          to: props.patientEmail,
          from: 'nourishedrx@nourishedrx.com',
          patientId: props.patientId,
          templateId: EMAIL_TEMPLATES['IU PRIMER EMAIL'],
          subject: 'IU PRIMER EMAIL',
          sender: authInfo.practitionerName,
          dynamicTemplateData: {
            memberName: props.patientName,
          },
          text: 'careplan launch',
        };
        await SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
          () => {
            props.communicationsMutate();
          },
          (e) => {
            console.log(`Error: Failed to send web survey email(s): ${(e as Error).toString()}`);
          }
        );
        payload = {
          to: props.patientEmail,
          from: 'nourishedrx@nourishedrx.com',
          patientId: props.patientId,
          templateId: EMAIL_TEMPLATES['APP DOWNLOAD EMAIL'],
          subject: 'APP DOWNLOAD EMAIL',
          sender: authInfo.practitionerName,
          dynamicTemplateData: {
            memberName: props.patientName,
          },
          text: 'careplan launch',
        };
        await SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
          () => {
            props.communicationsMutate();
          },
          (e) => {
            console.log(`Error: Failed to send web survey email(s): ${(e as Error).toString()}`);
          }
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      Referral.launchCarePlan(
        props.orgId!,
        props.patientId!,
        props.planDefinition!.id!,
        moment().toISOString(),
        props.planDefinition!.name!,
        props.planDefinition!.purpose!,
        [
          {
            reference: 'Practitioner/' + import.meta.env.VITE_DEFAULT_PRACTITIONER_WA_ID, //update to WA pool
            display: 'WA POOL',
          },
        ], //inital OA
        [
          {
            reference: 'Practitioner/' + import.meta.env.VITE_DEFAULT_PRACTITIONER_RD_ID, //update to RD pool
            display: 'RD POOL',
          },
        ], // RD
        // Cast, not changed: `careCoordinators` is declared a one-element tuple and this
        // sole caller passes `null`. Supplying a member would alter the CareTeam written.
        null as unknown as [{ reference: string; display: string }], //CC
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        },
        authInfo.practitionerName,
        fhirProxyClient
      ).then(async (_response) => {
        await updatePatientWithCTStatus(props.fhirPatient!, 'ASSIGNED'); // We always have WA assigned
        props.onLaunch(); //callback
        setLoading(false);
      });
    } catch (error) {
      console.log('Error in launchCarePlan():', (error instanceof Error ? error.message : String(error)));
      setLoading(false);
      throw error;
    }
  };

  // SUSPECTED DEFECT — `Patient.identifier` is `0..*` in FHIR and is read
  // unguarded here, so a Patient carrying no identifiers throws inside the
  // `.then` of `launchCarePlan` — after the CarePlan has already been written.
  // The care plan would exist while the patient's care-team-status stayed
  // stale. Reported, not fixed: `?? []` would silently skip the status update
  // and leave the same inconsistency without any signal. The `!` preserves the
  // existing throw.
  async function updatePatientWithCTStatus(fhirPatient: Patient, careTeamStatus: string) {
    const patientIdentifiers = fhirPatient.identifier!.map((identifier: Identifier) => {
      if (identifier.system === 'https://projectwell.io/fhir/identifiers/care-team-status') {
        identifier.value = careTeamStatus;
      }
      return identifier;
    });
    fhirPatient.identifier = patientIdentifiers;

    await FHIRUpdater('/Patient/' + fhirPatient.id, authInfo.token, fhirPatient);
  }

  const isLoading = loading || props.isRefreshing;
  const isEnabled = enableLaunch && !isLoading;

  return (
    <>
      {isLoading && <Loader active inline="centered" />}
      <List horizontal>
        {flatCarePlan && (
          <>
            <Popup
              flowing
              trigger={
                <Label color="orange" image>
                  <Icon name="paper plane" />
                  Completed
                </Label>
              }
              content={<Label>{props.planDefinition!.name}</Label>}
              on="hover"
            />
            <Popup
              flowing
              trigger={
                <Label color="grey" image>
                  {moment(flatCarePlan.startDate).fromNow()}
                </Label>
              }
              content={
                <Label>
                  <Icon name="clock" />
                  Last Updated By: {flatCarePlan.lastModifiedBy}.{' '}
                  {moment(flatCarePlan.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}
                </Label>
              }
              on="hover"
            />
          </>
        )}
        {!flatCarePlan && (
          <>
            <List.Item>
              <Popup
                flowing
                disabled={isLoading}
                trigger={
                  <Label
                    color={isEnabled ? 'blue' : 'grey'}
                    as={isEnabled ? 'a' : ''}
                    active={isEnabled}
                    image
                    // Cast, not a removed branch: `exactOptionalPropertyTypes` refuses the
                    // explicit `undefined` this ternary yields, and that arm is what
                    // disables the click while a launch is in flight.
                    onClick={isLoading ? (undefined as unknown as NonNullable<LabelProps['onClick']>) : launch}
                    style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                  >
                    <Icon name="paper plane" />
                    Create Care Plan
                  </Label>
                }
                content={<Label>{props?.planDefinition?.name}</Label>}
                on="hover"
              />
            </List.Item>
            <Checkbox disabled={isLoading} style={{ marginLeft: '4px' }} label="Accept Referral" onChange={onAccept} />
          </>
        )}
      </List>
    </>
  );
}

export default Launch;
